import type { ComponentType, ErrorInfo } from "react";
import type { Root } from "react-dom/client";
import { createRoot, hydrateRoot } from "react-dom/client";
import type { FormName } from "@searchui-searchforms/shared/config";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import { invariant } from "@searchui-searchforms/shared/utils";
import { createI18nInstance, getLanguageFromLocale } from "@/shared/i18n";
import { EN_LANGUAGE } from "@/shared/i18n/en-language";
import { logFCI } from "@/shared/lib/fci";
import { makeQueryClient } from "@/shared/queryClient";
import type { FormInstanceStore } from "@/shared/types/custom-ancestry";
import { RenderEntry } from "../../lib/render-entries";
import { SFS_INSTANCE_ID_ATTR, SFS_RENDER_ROOT_ATTR } from "./constants";
import type { AttachProps, ComponentProps } from "./types";

/**
 * Finds the render root element within the container.
 *
 * With SSR, the container includes a dedicated render root element marked with
 * `SFS_RENDER_ROOT_ATTR`. This allows us to inject inline links for required
 * resources (CSS, preloads) that the browser can fetch immediately without breaking a hydration.
 *
 * With CSR, there's no SSR markup, so we mount directly to the container itself.
 */
const findRenderRoot = (container: HTMLElement): { el: HTMLElement; renderRoot: HTMLElement | null } => {
    const renderRoot = container.querySelector<HTMLElement>(`[${SFS_RENDER_ROOT_ATTR}]`);
    return { el: renderRoot ?? container, renderRoot };
};

const detectLanguage = (formStore: FormInstanceStore | undefined): string => {
    if (formStore?.locale) {
        return getLanguageFromLocale(formStore.locale);
    }
    if (document.documentElement.lang) {
        return getLanguageFromLocale(document.documentElement.lang);
    }
    if (navigator.language) {
        return getLanguageFromLocale(navigator.language);
    }
    return EN_LANGUAGE;
};

type AttachOptions = {
    onReady?: () => void;
    /**
     * Callback invoked when an error occurs during rendering.
     * Allows the host application to handle errors (e.g., display in ErrorBoundary).
     */
    onError?: (error: unknown, errorInfo?: ErrorInfo) => void;
};

/**
 * Creates an attach function for mounting a React component to a DOM container.
 *
 * Supports both client-side rendering (CSR) and server-side rendering (SSR) hydration.
 * When SSR markup is detected (container has child nodes), it hydrates the existing HTML
 * using the dehydrated query state from SSR. Otherwise, it creates a fresh React root.
 */
export const makeAttach = (Component: ComponentType<ComponentProps>, formName: FormName) => {
    const roots = new WeakMap<HTMLElement, Root>();
    const queryClient = makeQueryClient();

    const handleRecoverableError =
        (formMetadataParams: AttachProps["formMetadataParams"]) => (error: unknown, errorInfo: ErrorInfo) => {
            logFCI({
                errorType: FCI_ERROR.TransparentSubstitution,
                errorInfo: `Recoverable MF render error: ${error}`,
                details: {
                    stack: error instanceof Error ? error.stack : undefined,
                    componentStack: errorInfo.componentStack ?? undefined,
                    formName,
                    ...formMetadataParams,
                },
            });
        };

    return async function attach(container: HTMLElement, props: AttachProps, opts?: AttachOptions) {
        const { onReady, onError } = opts ?? {};

        try {
            const baseProps = { ...props, queryClient, onReady };

            const { el, renderRoot } = findRenderRoot(container);
            const instanceId = el.getAttribute(SFS_INSTANCE_ID_ATTR);

            // Create i18n instance once
            const store = instanceId ? window.ancestry?.search?.forms?.instances?.[instanceId] : undefined;

            const language = detectLanguage(store);
            const i18nInstance = await createI18nInstance({
                language,
                resources: store?.i18nResources,
            });

            let root = roots.get(el);

            if (!root) {
                // A dedicated render-root element is emitted only when SSR actually rendered
                // the form. On an SSR failure the container holds only sibling nodes (e.g. the
                // manifest <script>), so `hasChildNodes()` would misclassify it as hydratable —
                // gate on the render root instead and fall back to CSR otherwise.
                const hasSsrMarkup = renderRoot !== null;

                if (hasSsrMarkup) {
                    invariant(store, "Store must be present");

                    const hydrationProps = {
                        ...baseProps,
                        dehydratedState: store.dehydratedState,
                        i18n: i18nInstance,
                    };
                    root = hydrateRoot(
                        el,
                        <RenderEntry
                            Component={Component}
                            componentProps={hydrationProps}
                            onError={onError}
                            formName={formName}
                        />,
                        {
                            onRecoverableError: handleRecoverableError(props.formMetadataParams),
                        },
                    );
                } else {
                    root = createRoot(el, {
                        onRecoverableError: handleRecoverableError(props.formMetadataParams),
                    });
                    root.render(
                        <RenderEntry
                            Component={Component}
                            componentProps={{ ...baseProps, i18n: i18nInstance }}
                            onError={onError}
                            formName={formName}
                        />,
                    );
                }

                roots.set(el, root);
            } else {
                // "Hydrate once, render later" pattern:
                // After initial hydration (or CSR mount), subsequent attach() calls reuse the same root.
                // This happens when VendorIsland forwards prop updates via instance.update().
                // We use root.render() instead of re-hydrating to avoid React warnings about
                // hydrating already-hydrated content.
                root.render(
                    <RenderEntry
                        Component={Component}
                        componentProps={{ ...baseProps, i18n: i18nInstance }}
                        onError={onError}
                        formName={formName}
                    />,
                );
            }

            return {
                update(nextProps: AttachProps) {
                    root!.render(
                        <RenderEntry
                            Component={Component}
                            componentProps={{ ...nextProps, queryClient, i18n: i18nInstance }}
                            onError={onError}
                            formName={formName}
                        />,
                    );
                },
                unmount() {
                    root!.unmount();
                    roots.delete(el);
                },
            };
        } catch (error) {
            logFCI({
                errorType: FCI_ERROR.Main,
                errorInfo: `MF attach() failed: ${error}`,
                details: {
                    stack: error instanceof Error ? error.stack : undefined,
                    formName,
                    ...props.formMetadataParams,
                },
            });
            onError?.(error);
            throw error;
        }
    };
};
